<script setup lang="ts">
import {useNuxtApp, useRuntimeConfig} from '#app';
import {ref, computed, onMounted, onBeforeUnmount} from 'vue';
import type { Ref } from 'vue'
import {CookieService} from "../../services";
import {useI18n} from "#imports";
import {backIcon, checkIcon} from "../../config";
import {settingsAdsDisabledCookieName, settingsCookieConsentCookieName} from "../../config/cookies";

const { t } = useI18n()
const config = useRuntimeConfig();
const { $analytics } = useNuxtApp();

const icons = {
	back: backIcon,
	check: checkIcon,
};

const isActive = ref(false);
const currentContent: Ref<string | null> = ref(null);
const visibleCookies: Ref<number | null> = ref(null);
const appearTimeout: Ref<string | number | null> = ref(null);

// Whether Google Analytics is enabled
const gaEnabled = ref(true);

// Whether Google AdSense is enabled
const gasEnabled = ref(true);

const text = {
	title: t('cookie_banner.title'), // The title to show above the banner content text
	accept: t('cookie_banner.accept'), // The text of the 'accept' button
	settings: t('cookie_banner.settings'), // The text for the button to toggle the custom selection
	privacy: t('cookie_banner.privacy'), // The text for the button to show the privacy policy
	imprint: t('cookie_banner.imprint'), // The text for the button to show the imprint
	back: t('cookie_banner.back'), // The text for the back button on the custom selection page
	content: t('cookie_banner.content'),
	custom_content: t('cookie_banner.custom_content')
};

const modalTitle = computed(() => {
	if (currentContent.value === 'privacy') {
		return `${text.title}: ${t('cookie_banner.privacy')}`;
	}
	if (currentContent.value === 'imprint') {
		return `${text.title}: ${t('cookie_banner.imprint')}`;
	}

	return text.title;
})

const cookies = computed(() => {
	const essentialCookies = [
		{
			title: t('cookie_banner.cookies.authentication.title'),
			text: t('cookie_banner.cookies.authentication.text')
		},
		{
			title: t('cookie_banner.cookies.settings.title'),
			text: t('cookie_banner.cookies.settings.text')
		},
		{
			title: t('cookie_banner.cookies.stripe.title'),
			text: t('cookie_banner.cookies.stripe.text')
		},
	];

	return [
		{
			title: t('cookie_banner.cookies.essential.title'),
			text: t('cookie_banner.cookies.essential.text'),
			cookies: essentialCookies,
			disabled: true,
			value: true,
			handler: () => {
			}
		},
		{
			title: t('cookie_banner.cookies.statistics.title'),
			text: t('cookie_banner.cookies.statistics.text'),
			cookies: [
				{
					title: t('cookie_banner.cookies.statistics.ga.title'),
					text: t('cookie_banner.cookies.statistics.ga.text'),
				},
				{
					title: t('cookie_banner.cookies.statistics.gtm.title'),
					text: t('cookie_banner.cookies.statistics.gtm.text'),
				},
			],
			disabled: false,
			value: gaEnabled.value,
			handler: toggleGoogleAnalytics
		},
		{
			title: t('cookie_banner.cookies.ads.title'),
			text: t('cookie_banner.cookies.ads.text'),
			cookies: [
				{
					title: t('cookie_banner.cookies.ads.gas.title'),
					text: t('cookie_banner.cookies.ads.gas.text'),
				}
			],
			disabled: false,
			value: gasEnabled.value,
			handler: toggleGoogleAdSense
		}
	];
})

const gaDisableCookieName = computed(() => {
	return config.public.gtmId ? `ga-disable-${config.public.gtmId}` : null;
})

onMounted(() => {
	if (hasCookie(settingsCookieConsentCookieName)) {
		gaEnabled.value = !hasCookie(gaDisableCookieName);
		if (gaEnabled.value) {
			enableGoogleAnalytics();
		} else {
			disableGoogleAnalytics();
		}

		gasEnabled.value = !hasCookie(settingsAdsDisabledCookieName);
		if (gasEnabled.value) {
			enableGoogleAdSense();
		} else {
			disableGoogleAdSense();
		}
	}

	// Add a timeout to not immediately show the banner on page load
	appearTimeout.value = setTimeout(() => {
		isActive.value = !hasCookie(settingsCookieConsentCookieName);
	}, 3000);
});

onBeforeUnmount(() => {
	if (appearTimeout.value) {
		clearTimeout(appearTimeout.value!);
	}
});

function showContent(type) {
	currentContent.value = type;
}

function hideMoreInfo() {
	currentContent.value = null;
}

/**
 * Accepts the cookie banner and hides it.
 */
function accept() {
	// We set a shorter cookie persistence interval when any critical setting is disabled,
	// so the user can rethink his decision more often
	let expirationDays = 365;

	if (gaEnabled.value) {
		enableGoogleAnalytics();
	} else {
		disableGoogleAnalytics();
		expirationDays = 90;
	}

	if (gasEnabled.value) {
		enableGoogleAdSense();
	} else {
		disableGoogleAdSense();
		expirationDays = 90;
	}

	setCookie(settingsCookieConsentCookieName, expirationDays);
	isActive.value = false;
}

function toggleGoogleAnalytics(value) {
	gaEnabled.value = value;
}

function enableGoogleAnalytics() {
	if (config.public.gtmId) {
		$analytics.enable()
		removeCookie(gaDisableCookieName)
	}
}

function disableGoogleAnalytics() {
	if (config.public.gtmId) {
		$analytics.disable()
		setCookie(gaDisableCookieName)
	}
}

function toggleGoogleAdSense(value) {
	gasEnabled.value = value;
}

function enableGoogleAdSense() {
	// Potentially activate any ad scripts here
	removeCookie(settingsAdsDisabledCookieName)
}

function disableGoogleAdSense() {
	setCookie(settingsAdsDisabledCookieName)
}

function hasCookie(name) {
	return (new CookieService(name)).get() === 'true';
}

function setCookie(name, expirationDays = 365) {
	(new CookieService(name)).onAllDomains().path('/').expiresInDays(expirationDays).set('true');
}

function removeCookie(name) {
	(new CookieService(name)).onAllDomains().path('/').remove();
}

</script>

<template>
	<modal v-model="isActive" :title="modalTitle" persistent :show-close-button="false" :mobile-fullscreen="false" :closeable="false" button-break-column="xs">
		<template v-if="currentContent === 'settings'">
			<p v-if="text.custom_content" class="text-sm">{{ text.custom_content }}</p>
			<div v-for="(cookie, key) in cookies" :key="key" class="py-24" :class="{'border-b': key < cookies.length - 1}">
				<div>
					<form-switch :disabled="cookie.disabled" :model-value="cookie.value" @update:modelValue="cookie.handler" :on-text="false" :off-text="false">
                        <span class="flex flex-column">
                            <span v-if="cookie.title" class="font-bold text-sm mb-3">{{ cookie.title }}</span>
                            <span v-if="cookie.text" class="text-sm leading-tight">{{ cookie.text }}</span>
                            <span>
                                <btn class="pl-0" plain small color="primary" v-if="visibleCookies !== key"
									 @click="visibleCookies = key">{{ t('cookie_banner.show_cookies') }}</btn>
                                <btn class="pl-0" plain small color="primary" v-else @click="visibleCookies = null">{{ t('cookie_banner.hide_cookies') }}</btn>
                            </span>
                        </span>
					</form-switch>
					<info-list v-if="visibleCookies === key" x-small class="ml-64">
						<info-list-item v-for="(cookieItem, cookieItemKey) in cookie.cookies" :key="cookieItemKey" :icon="false">
                                        <span class="flex flex-column">
                                            <b>{{ cookieItem.title }}:</b>
                                            <span v-html="cookieItem.text"></span>
                                        </span>
						</info-list-item>
					</info-list>
				</div>
			</div>
		</template>
		<server-content v-else-if="currentContent === 'privacy'" :action="'/v1/settings/privacy'" as-guest access="value"/>
		<server-content v-else-if="currentContent === 'imprint'" :action="'/v1/settings/imprint'" as-guest access="value"/>
		<p v-else-if="text.content" class="text-sm mb-0">{{ text.content }}</p>

		<template #footer>
			<wrapper v-if="!currentContent || currentContent === 'settings'" class="my-8" center>
				<btn @click="showContent('privacy')" class="text-gray-450 hover:text-primary" x-small plain>{{ text.privacy }}</btn>
				<btn @click="showContent('imprint')" class="text-gray-450 hover:text-primary" x-small plain>{{ text.imprint }}</btn>
			</wrapper>
		</template>

		<template #button>
			<btn v-if="currentContent" large @click="hideMoreInfo">
				<Icon :icon="icons.back"/>
				<span>{{ text.back }}</span>
			</btn>
			<btn v-else large @click="showContent('settings')">
				<span>{{ text.settings }}</span>
			</btn>

			<btn color="success" large @click="accept">
				<Icon :icon="icons.check"/>
				<span>{{ text.accept }}</span>
			</btn>
		</template>
	</modal>
</template>
